import { ChangeDetectorRef, Component, resolveForwardRef } from '@angular/core';
import { inject, AfterViewInit } from '@angular/core';
import { SnackService } from '../../../service/snack.service';
import { TrackService } from '../../../service/track.service';
import { Step, Stream, Track, UserStreamResults, UserTrack } from '../../../interfaces/trackdata';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AiService } from '../../../service/ai.service';
import { ReportService } from '../../../service/report.service';
import { StreamService } from '../../../service/stream.service';
import { UserRagComponent } from '../generic-stream/user-rag/user-rag.component';

@Component({
    selector: 'app-new-track-run',
    standalone: false,
    templateUrl: './new-track-run.component.html',
    styleUrl: './new-track-run.component.scss'
})
export class NewTrackRunComponent implements AfterViewInit {
    // demoURL: http://localhost:5261/new-track-run/Clu-4-HBY?streamId=J1S-8At2nL&stepId=jKHo2wIzi&roundId=b522ce4b-3503-4b40-8baf-2170649bdf9b
    // basic Idea: http://localhost:5261/new-track-run/Clu-4-HBY?streamId=J1S-8At2nL&stepId=MMUT2vQla&roundId=b522ce4b-3503-4b40-8baf-2170649bdf9b

    // Injections
    cd = inject(ChangeDetectorRef);
    snack = inject(SnackService);
    trackService = inject(TrackService);
    route = inject(ActivatedRoute);
    router = inject(Router);
    dialog = inject(MatDialog);
    aiService = inject(AiService);
    report = inject(ReportService);
    streamService = inject(StreamService);

    // Variables
    loading: boolean = false;
    nextStreamStepDisabledStatus: boolean = true;
    urlTrackId: string = "";
    urlStreamId: string = "";
    urlStepId: string = "";
    urlRoundId: string = "";
    currentStepNumber: number = 0;
    currentMaxSteps: number = 0;
    progress: number = 0;
    runClosingPrompt: boolean = false;
    initialReady: boolean = false;

    // Data Objects
    currentTrackData: Track | undefined;
    currentUserTrackData: UserTrack | undefined;
    currentStreamData: any | undefined;
    currentUserStreamData: UserStreamResults | undefined;
    currentStepData: Step | undefined;
    currentUserStepData: any;

    async ngAfterViewInit(): Promise<void> {
        this.loading = true;
        // Load URL Data
        const url = this.router.url;
        let decodedUrl = decodeURIComponent(url);

        let counter = 0
        while (decodedUrl !== decodeURIComponent(decodedUrl) && counter < 20) {
            decodedUrl = decodeURIComponent(decodedUrl);
            counter++;
        }
        decodedUrl = decodedUrl.replace("%3F", "?");
        decodedUrl = decodedUrl.replaceAll("%3D", "=");

        const [path, queryString] = decodedUrl.split('?');
        const queryParams = new URLSearchParams(queryString);

        this.urlTrackId = decodedUrl.split("?")[0];
        this.urlTrackId = this.urlTrackId.split("run/")[1];

        this.urlStreamId = queryParams.get("streamId") || "";
        this.cd.detectChanges();
        this.urlStepId = queryParams.get("stepId") || "";
        this.cd.detectChanges();
        this.urlRoundId = queryParams.get("roundId") || "";
        this.cd.detectChanges();

        if (this.urlStreamId === "" || this.urlStepId === "" || this.urlRoundId === "") {
            if (!this.urlStreamId) console.log("Missing streamId:", this.urlStreamId);
            if (!this.urlStepId) console.log("Missing stepId:", this.urlStepId);
            if (!this.urlRoundId) console.log("Missing roundId:", this.urlRoundId);

            alert("The requested URL is invalid.");
            return;
        }
        this.cd.detectChanges();
        // ========= Track Data
        let data = await this.trackService.initialTrackDataLoad(this.urlTrackId, this.urlRoundId);
        if (data == false) return;
        // console.log(data);

        [this.currentTrackData, this.currentUserTrackData] = await Promise.all([
            await data.trackProcessData.find(
                (track: Track) =>
                    track.id === this.urlTrackId
            ),
            await data.userProcessData.find(
                (userTrack: UserTrack) =>
                    userTrack.trackId === this.urlTrackId
                    && userTrack.roundId === this.urlRoundId
            )
        ]);
        // ========= Stream Data
        this.currentStreamData = await this.currentTrackData?.streams.find(
            (stream: Stream) => stream.id === this.urlStreamId
        );
        this.currentUserStreamData = await this.currentUserTrackData?.results?.find(
            (result: UserStreamResults) => result.streamId == this.urlStreamId
        );
        console.log(this.currentUserTrackData);

        // ========= Step Data
        this.currentStepData = await this.currentStreamData?.steps.find(
            (step: Step) => step.id === this.urlStepId
        );
        // console.log("Step", this.currentStepData);

        this.cd.detectChanges();
        this.currentUserStepData = await this.currentUserStreamData?.stepResults.find(
            (stepResult) => stepResult.stepId === this.urlStepId
        );
        // ========= Communication History
        // await this.trackService.loadComHistory(this.urlStepId, this.urlRoundId);
        this.calculateProgressAndSetStepNumbers();

        this.loading = false;
        this.initialReady = true;
        this.cd.detectChanges();
    }
    async oneStepForeward() {
        this.runClosingPrompt = true;
    }
    async openDocumentUpload() {
        let dia = await this.dialog.open(UserRagComponent, {
            panelClass: "small-dialog",
            autoFocus: false,
            disableClose: true
        });
        dia.afterClosed().subscribe(result => {
            if (result) {
                console.log('Document upload dialog closed with result:', result);
            }
        });
    }
    async saveData(data: { "input": string, "type": any, "extendedData"?: any } | undefined) {
        this.runClosingPrompt = false;
        if (!data) {
            console.log("TCL: NewTrackRunComponent -> saveData -> ", "no Data")
            return;
        }

        // Check if a result exists and ask for confirmation before overriding
        let results = await this.currentUserTrackData?.results?.find(result => result.streamId === this.urlStreamId);
        if (results) {
            let stepResult = await results.stepResults.find(step => step.stepId === this.urlStepId);
            if (stepResult) {
                let choise = await confirm("Do you want to override the existing Data?");
                if (choise === false) {
                    return;
                }
                if (stepResult.fileId && stepResult.fileId !== "") {
                    this.trackService.deleteFileFromTrackResult(this.urlRoundId, stepResult.fileId, stepResult.file?.fileName);
                }
            }
        }

        let stepResultToSave: any;
        if (data.type !== "save-only") {
            // Run this if closing Prompt exists else skip this part
            this.loading = true;
            let result = await this.aiService.getAiResponse(this.urlStreamId, this.urlStepId, data.input, data.type, 0, this.urlRoundId)
            this.loading = false;
            if (!result?.promptResult || !result.promptResult[0]) {
                console.log("Error in AI response ==> RESTART");
                return;
            }
            stepResultToSave = result.promptResult[0];
            if (this.urlStepId === "MMUT2vQla") {
                let updateName = await this.trackService.updateTrackName(this.urlRoundId, result.promptResult[0].name);
                if (updateName === true && this.currentUserTrackData) {
                    this.currentUserTrackData.name = result.promptResult[0].name;
                }
            }
            if (this.urlStepId === "jKHo2wIzi") {
                stepResultToSave.target_user = data.extendedData;
            }
        } else {
            // Only run this when no closing promopt is needed
            try {
                stepResultToSave = JSON.parse(data.input);
            } catch (error) {
                stepResultToSave = data.input;

            }
        }
        // console.log("RESULT ", stepResultToSave);

        await this.trackService.addStepResult(this.urlRoundId, this.urlStreamId, this.urlStepId, stepResultToSave, this.currentStepData?.name || '');
        this.snack.info({ msg: { headline: 'Success', text: 'Step result generated' } });
        this.findNextStepIdByCurrentStepId();
    }
    calculateProgressAndSetStepNumbers() {
        this.currentMaxSteps = this.currentStreamData?.steps?.length;
        this.currentStreamData?.steps?.forEach((step: Step, index: number) => {
            if (step.id == this.urlStepId) {
                this.currentStepNumber = index + 1;
            }
        });
        this.progress = (this.currentStepNumber / this.currentMaxSteps) * 100;
    }
    async findPreviusStepIdByCurrentStepId() {
        let firstStream: boolean = false;
        let firstStep: boolean = false;
        let streamIndex: number = 0;
        let stepIndex: number = 0;

        let nextStreamId: string = this.urlStreamId;
        let nextStepId: string = "";

        for (let i = 0; i < this.currentTrackData!.streams?.length; i++) {
            let stream = this.currentTrackData?.streams[i];
            if (stream?.id === this.urlStreamId) {
                streamIndex = i;
                // if first stream set variable as true
                if (i === 0) {
                    firstStream = true;
                }

                // Steparea
                for (let o = 0; o < stream.steps.length; o++) {
                    let step = stream.steps[o];
                    if (step.id === this.urlStepId) {
                        stepIndex = o;
                        if (o === 0) {
                            firstStep = true;
                        }
                    }
                }
            }

            if (this.urlStepId === "closing-stream") {
                firstStep = true;
            }
        }


        // === CASES
        // when firstStream && firstStep === true; => go /home
        // when firstStream === false && firstStep === true; => navigate to last Step in prev Stream
        // else go one Step back

        if (firstStream === true && firstStep === true) {
            this.router.navigate(['/home/user']);
            return;
        }

        if ((firstStep === false && firstStream === true)) {
            console.log("Common Case");

            nextStreamId = this.urlStreamId;
            nextStepId = this.currentTrackData?.streams[streamIndex]?.steps[stepIndex - 1]?.id || this.urlStepId;
        }
        if ((firstStep === true && firstStream === false)) {
            nextStreamId = this.currentTrackData?.streams[streamIndex - 1]?.id || this.urlStreamId;
            nextStepId = "closing-stream";
        }
        if (this.urlStepId === "closing-stream") {
            nextStreamId = this.currentTrackData?.streams[streamIndex - 1]?.id || this.urlStreamId;
            nextStepId = this.currentTrackData?.streams[streamIndex - 1]?.steps[this.currentTrackData?.streams[streamIndex - 1]?.steps.length - 1]?.id || this.urlStepId;
        }

        this.urlStepId = nextStepId;
        this.cd.detectChanges();
        this.urlStreamId = nextStreamId;
        this.cd.detectChanges();
        this.nextStreamStepDisabledStatus = true;

        // console.log("firstStream", firstStream);
        // console.log("firstStep", firstStep);
        // console.log("streamIndex", streamIndex);
        // console.log("stepIndex", stepIndex);
        // console.log("nextStreamId", nextStreamId);
        // console.log("nextStepId", nextStepId);

        this.currentStepData = await this.currentStreamData?.steps.find(
            (step: Step) => step.id === this.urlStepId
        );

        this.router.navigate(['new-track-run', this.urlTrackId], {
            queryParams: {
                streamId: nextStreamId,
                stepId: nextStepId,
                roundId: this.urlRoundId
            },
            onSameUrlNavigation: "reload"
        });
    }
    async findNextStepIdByCurrentStepId() {
        // in welchem Stream befinden wir uns
        // ==> letzter || nicht letzter
        // in dem Stream heraus finden in welchem Step befinden wir uns
        // ==> letzter || nicht letzter

        // 1. Wenn nicht letzter strea und nicht letzter Step oder letzer Stream und nicht letzter Step
        // ==> nächsten Step (gemäß index)
        // 2. Wenn nicht letzter Stream aber letzter Step in Stream
        // ==> ersten Step auzs nächsten Stream
        // 3. Wenn letzter Stream und letzter Step
        // ==> Track finished & show Result-Screen

        // AI Chat schließen
        this.aiService.showAiChat.next(false);
        this.cd.detectChanges();

        let lastStream: boolean = false;
        let lastStep: boolean = false;
        let streamIndex: number = 0;
        let stepIndex: number = 0;

        let nextStreamId: string = this.urlStreamId;
        let nextStepId: string = "";

        for (let i = 0; i < this.currentTrackData!.streams?.length; i++) {
            let stream = this.currentTrackData?.streams[i];
            if (stream?.id === this.urlStreamId) {
                streamIndex = i;
                // if last stream set variable as true
                if (this.currentTrackData!.streams?.length - 1 === i) {
                    lastStream = true;
                }

                // Steparea
                for (let o = 0; o < stream.steps.length; o++) {
                    let step = stream.steps[o];
                    if (step.id === this.urlStepId) {
                        stepIndex = o;
                        if (stream.steps.length - 1 === o) {
                            lastStep = true;
                        }
                    }
                }
            }

            if (this.urlStepId === "closing-stream") {
                lastStep = true;
            }
        }

        // Most common case
        if (lastStep === false) {
            nextStepId = this.currentTrackData?.streams[streamIndex].steps[stepIndex + 1].id || this.urlStreamId
        }

        if (lastStep === true && lastStream === false) {
            nextStepId = "closing-stream";
        }
        if (lastStep === true && lastStream === false && this.urlStepId === "closing-stream") {
            nextStreamId = this.currentTrackData?.streams[streamIndex + 1]?.id || this.urlStreamId;
            nextStepId = this.currentTrackData?.streams[streamIndex + 1]?.steps[0]?.id || this.urlStepId;
        }

        // console.log("lastStream", lastStream);
        // console.log("lastStep", lastStep);
        // console.log("streamIndex", streamIndex);
        // console.log("stepIndex", stepIndex);
        // console.log("nextStreamId", nextStreamId);
        // console.log("nextStepId", nextStepId);

        this.urlStepId = nextStepId;
        this.cd.detectChanges();
        this.urlStreamId = nextStreamId;
        this.cd.detectChanges();
        this.nextStreamStepDisabledStatus = true;


        if (lastStep === true && lastStream === true) {
            // Finishes all go back to home
            this.router.navigate(['/home/user']);
            return;
        }

        this.currentStepData = await this.currentStreamData?.steps.find(
            (step: Step) => step.id === this.urlStepId
        );

        this.router.navigate(['new-track-run', this.urlTrackId], {
            queryParams: {
                streamId: nextStreamId,
                stepId: nextStepId,
                roundId: this.urlRoundId
            },
            onSameUrlNavigation: "reload"
        });
    }

    async saveAndExit() {
        this.report.saveReport(this.urlRoundId, 'Save & Exit');
        this.router.navigate(['/home/user']);
    }
}
